'use client';

import { createContext, useMemo, useContext, useState, useCallback } from 'react';

import AccountTheme from '@hub/common/src/AccountTheme';
import type { Account, AccountThemeConfig, CitOrigin } from '@hub/common/src/types/cit-public-api';

export type CitThemeState = {
  citOrigin: CitOrigin;
  account?: Account | undefined;
  theme?: AccountThemeConfig | undefined;
};

export type CitThemeContextValue = {
  accountTheme: AccountTheme;
  setTheme: (state: Partial<CitThemeState>) => void;
};

export const CitThemeContext = createContext<CitThemeContextValue | null>(null);

export const useCitThemeClient = () => {
  const value = useContext(CitThemeContext);

  if (!value) {
    throw new Error('CitThemeProvider not found');
  }

  return value;
};

// providing just the citOrigin will use the default theme for components that are not by default account-specific but are account-specific later in the component tree
export default function CitThemeProvider({
  citOrigin,
  account,
  theme,
  children
}: CitThemeState & { children: React.ReactNode }) {
  const [state, setState] = useState<CitThemeState>({
    citOrigin,
    account,
    theme
  });

  // need to be able to update only a part of the state because we dont want to overwrite the test theme from the entry modal carousel
  const setTheme = useCallback((update: Partial<CitThemeState>) => {
    setState((prev) => ({ ...prev, ...update }));
  }, []);

  const [renderedTheme, setRenderedTheme] = useState(theme);

  // a test theme change refreshes this layout in place rather than remounting it, so the state
  // seeded above has to follow the prop
  if (renderedTheme !== theme) {
    setRenderedTheme(theme);
    setState((prev) => ({ ...prev, theme }));
  }

  const value = useMemo(() => {
    const accountTheme = new AccountTheme(state.citOrigin, state.account, state.theme);
    return { accountTheme, setTheme };
  }, [state.citOrigin, state.account, state.theme, setTheme]);

  return <CitThemeContext.Provider value={value}>{children}</CitThemeContext.Provider>;
}
